import { Box, useToken } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { IoEyeOutline } from 'react-icons/io5';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import Show from '@/components/Molecules/Show';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import { colors } from '@/components/ui/theme';
import {
	useGetAllDenialSupports,
	useGetDenialSupportProducts,
	useGetSingleDenialSupport,
} from '@/hooks/service-hooks/denialSupport';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import usePermission from '@/hooks/usePermission';
import { getAllDenialSupports } from '@/services/denialSupport';
import type {
	DenialSupportProduct,
	DenialSupportRaw,
} from '@/types/denialSupport';
import type { FilterType } from '@/types/table';
import { formatDate } from '@/utils';
import { getDisplayFromArray } from '@/utils/denials';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { mapStatus, statusOptions } from '../constants';
import DenialSupportDetails from '../DenialSupportDetails/DenialSupportDetails';
import { columns } from './columns';

const getProductDisplay = (products: DenialSupportProduct[]): string => {
	return getDisplayFromArray(
		products,
		(item) => item.ta_name,
		'Multiple Products',
		'-',
	);
};

const CLINIC_KEY = '_ta_practice_value';

const DenialSupportTable = () => {
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);

	const statusFiltersWithAll = useMemo(
		() => [{ value: '', label: 'All' }, ...statusOptions],
		[],
	);

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: true,
		status: '',
		globalFilters: JSON.stringify(
			defaultClinicIds.length
				? [
						{
							key: CLINIC_KEY,
							values: defaultClinicIds,
						},
					]
				: [],
		),
		sort: '',
	});

	const [modalInformation, setModalInformation] = useState<{
		mode: 'details' | null;
		data: string | null;
	}>({
		mode: null,
		data: null,
	});

	const { data, isFetching, refetch } = useGetAllDenialSupports(filters);
	const { data: denialSupportProducts } = useGetDenialSupportProducts();

	const { data: denialDetails, isFetching: isDenialDetailsLoading } =
		useGetSingleDenialSupport(
			modalInformation.mode === 'details'
				? (modalInformation.data as string)
				: null,
			modalInformation.mode === 'details',
		);

	const transformedData = useMemo(() => {
		if (!data?.items) return [];

		return data.items.map((item) => {
			const status = mapStatus(item.ta_appealstatus);
			const statusColor = status?.color || colors.defaultGray;
			const statusLabel = status?.label || 'Unknown';
			const products = item.products || [];

			return {
				id: item.ta_denialsid,
				'clinic.name': (
					<FaceliftTruncatedTextWithTooltip
						text={item.clinic?.name || '-'}
						maxLength={30}
					/>
				),
				'patient.fullname': (
					<FaceliftTruncatedTextWithTooltip
						text={item.patient?.fullname || '-'}
						maxLength={30}
					/>
				),
				'products.ta_name': (
					<FaceliftTruncatedTextWithTooltip
						text={getProductDisplay(products)}
						maxLength={30}
					/>
				),
				ta_denial_date: item.ta_denial_date
					? formatDate(item.ta_denial_date)
					: '-',
				ta_amountbilled: `$${item.ta_amountbilled?.toFixed(2) || '0.00'}`,
				ta_appealstatus: (
					<Box
						bg={statusColor}
						borderRadius={4}
						color="white"
						height="24px"
						px={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight={500}
						fontSize="sm"
					>
						<FaceliftTruncatedTextWithTooltip
							text={statusLabel}
							maxLength={20}
						/>
					</Box>
				),
				subRows:
					products.length > 1
						? products.map((product) => ({
								id: item.ta_denialsid,
								'products.ta_name': (
									<FaceliftTruncatedTextWithTooltip
										text={product.ta_name}
										maxLength={30}
									/>
								),
							}))
						: [],
			};
		});
	}, [data]);

	const transformedDataToExport = (data: DenialSupportRaw[]) => {
		if (!data) return [];

		return data.map((item) => {
			const products = item.products || [];
			return {
				id: item.ta_denialsid,
				'clinic.name': item.clinic?.name || '-',
				'patient.fullname': item.patient?.fullname || '-',
				'products.ta_name': getProductDisplay(products),
				ta_denial_date: item.ta_denial_date
					? formatDate(item.ta_denial_date)
					: '-',
				ta_amountbilled: `$${item.ta_amountbilled?.toFixed(2) || '0.00'}`,
				ta_appealstatus: mapStatus(item.ta_appealstatus).label,
			};
		});
	};

	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {
		const mappedFilters = mapGlobalFilters(globalFilters || []);

		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(columnsFilters),
			search,
			enabled: true,
			status: status?.value,
			globalFilters: JSON.stringify(mappedFilters),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	const handleViewDetails = (denialId: string) => {
		setModalInformation({
			mode: 'details',
			data: denialId,
		});
	};

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null });
	};

	const exportAllAsCSV = async () => {
		try {
			const data = await getAllDenialSupports(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);
			const transformedData = transformedDataToExport(data?.items || []);
			generateCSVFromTable(columns, transformedData, `DenialSupport-all`, true);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	const exportAllAsExcel = async () => {
		try {
			const data = await getAllDenialSupports(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(data?.items || []);
			generateExcelFromTable(
				columns,
				transformedData,
				`DenialSupport-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	const productFilterOptions = useMemo(() => {
		if (!denialSupportProducts?.products) return [];
		
		const uniqueProductNames = new Set<string>();
		const uniqueProducts = denialSupportProducts.products.filter((product: DenialSupportProduct) => {
			if (!product.ta_name) return false;
			if (uniqueProductNames.has(product.ta_name)) {
				return false; 
			}
			uniqueProductNames.add(product.ta_name);
			return true; 
		});
		
		return uniqueProducts
			.map((product: DenialSupportProduct) => ({
				id: product.ta_name,
				label: product.ta_name,
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [denialSupportProducts]);

	return (
		<>
			<Box
				p={{ base: 4, md: 8 }}
				py={{ base: 4, md: 6 }}
				bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			>
				<DataTable
					tableName="DenialSupport"
					loading={isFetching}
					items={transformedData}
					columns={columns}
					topLabel="Denial Support"
					totalCount={data?.meta.totalRecords || 0}
					onFilterApply={onFilterApply}
					requiredColumnsForExpand={['products.ta_name']}
					expandable
					fullscreen
					paginated
					hideShowColumn
					exportable
					excel
					csv
					exportAllAsCSV={exportAllAsCSV}
					exportAllAsExcel={exportAllAsExcel}
					searchable
					bordered
					statusFilters={statusFiltersWithAll}
					globalFilter={{
						title: 'Filters',
						clinics: true,
						items: [
							{
								name: 'Products',
								key: 'products.ta_name',
								options: productFilterOptions,
							},
						],
					}}
					actions={[
						{
							label: 'Details',
							icon: <IoEyeOutline />,
							actionFn: (item: ColumnData) => {
								const denialId = item.id as string;
								if (denialId) {
									handleViewDetails(denialId);
								}
							},
							show: () => hasPermission(['DenialSupport:Visualize']),
						},
					]}
				/>
			</Box>

			<FaceliftDialog
				showBackBtn={true}
				title="Details"
				open={modalInformation.mode === 'details'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				size="md"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
				}}
			>
				<Show when={isDenialDetailsLoading}>
					<FaceliftDataLoader
						title="Loading denial support details..."
						description="Please wait while we fetch the denial support information."
					/>
				</Show>
				<Show when={!isDenialDetailsLoading}>
					<DenialSupportDetails data={denialDetails || null} />
				</Show>
			</FaceliftDialog>
		</>
	);
};

export default DenialSupportTable;
